import React, { useState, useMemo } from 'react';
import type { AttendanceRecord, Shift, DayOfWeek, ScheduleEntry } from '../../types';
import { Icons } from '../ui/Icons';
import { formatDateYYYYMMDD, getWeekStartDate, getWeekDates, calculateMinutesDiff, calculateHours } from '../../utils/dateUtils';
import { DAY_INDEX_MAP } from '../../types';

interface WorkingHoursTabProps {
    attendanceRecords: AttendanceRecord[];
    personalSchedule: { [date: string]: ScheduleEntry };
    shifts: Shift[];
    weekStartDay: DayOfWeek;
    employeeId: string;
}

type PeriodType = 'weekly' | 'biweekly';

const WorkingHoursTab: React.FC<WorkingHoursTabProps> = ({
    attendanceRecords,
    personalSchedule,
    shifts,
    weekStartDay,
    employeeId
}) => {
    const [periodType, setPeriodType] = useState<PeriodType>('weekly');
    const [referenceDate, setReferenceDate] = useState(new Date());

    const weekStartDayIndex = useMemo(() => DAY_INDEX_MAP[weekStartDay], [weekStartDay]);

    const periodRange = useMemo(() => {
        const startDate = getWeekStartDate(referenceDate, weekStartDayIndex);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + (periodType === 'weekly' ? 6 : 13));
        return { startDate, endDate };
    }, [referenceDate, periodType, weekStartDayIndex]);

    const processedRecords = useMemo(() => {
        const records: any[] = [];
        const start = new Date(periodRange.startDate);
        const end = new Date(periodRange.endDate);
        
        let current = new Date(start);
        while (current <= end) {
            const dateStr = formatDateYYYYMMDD(current);
            const entry = personalSchedule[dateStr];
            
            if (entry && entry.status === 'ASSIGNED' && entry.shiftId && entry.isRejected !== true) {
                let startTime = '';
                let endTime = '';
                let schedStart = entry.assignedStartTime || '';
                let schedEnd = entry.assignedEndTime || '';

                const shiftTemplate = shifts.find(s => s.id === entry.shiftId);
                if (!schedStart || !schedEnd) {
                    if (shiftTemplate) {
                        schedStart = shiftTemplate.startTime;
                        schedEnd = shiftTemplate.endTime;
                    }
                }

                if (entry.isTimeEdited) {
                    startTime = entry.customStartTime || '';
                    endTime = entry.customEndTime || '';
                } else {
                    startTime = schedStart;
                    endTime = schedEnd;
                }

                if (startTime && endTime) {
                    const hours = calculateHours(startTime, endTime);
                    records.push({
                        id: `${dateStr}-${entry.shiftId}`,
                        date: dateStr,
                        shiftId: entry.shiftId,
                        actualStartTime: startTime,
                        actualEndTime: endTime,
                        scheduledStartTime: schedStart,
                        scheduledEndTime: schedEnd,
                        totalHours: hours,
                        extraHours: 0, // Extra hours logic can be complex, but for now we match the report's basic total
                        status: entry.isApproved ? 'APPROVED' : 'PENDING',
                        isRejected: false
                    });
                }
            }
            current.setDate(current.getDate() + 1);
        }
        return records.sort((a, b) => a.date.localeCompare(b.date));
    }, [personalSchedule, periodRange, shifts]);

    const summary = useMemo(() => {
        const totalHours = processedRecords.reduce((sum, r) => sum + (r.totalHours || 0), 0);
        const workDays = processedRecords.length;

        return {
            totalHours: totalHours.toFixed(2),
            extraHours: "0.00",
            workDays
        };
    }, [processedRecords]);

    const groupedRecords = useMemo(() => {
        if (periodType === 'weekly') {
            return [{ title: 'Records', records: processedRecords, totalHours: summary.totalHours }];
        }

        // Biweekly grouping
        const week1Start = new Date(periodRange.startDate);
        const week1End = new Date(week1Start);
        week1End.setDate(week1Start.getDate() + 6);
        const week1Str = formatDateYYYYMMDD(week1End);

        const week1Records = processedRecords.filter(r => r.date <= week1Str);
        const week2Records = processedRecords.filter(r => r.date > week1Str);

        const calcWeekHours = (recs: any[]) => 
            recs.reduce((sum, r) => sum + (r.totalHours || 0), 0).toFixed(2);

        return [
            { title: 'Week 1', records: week1Records, totalHours: calcWeekHours(week1Records) },
            { title: 'Week 2', records: week2Records, totalHours: calcWeekHours(week2Records) }
        ];
    }, [processedRecords, periodType, periodRange, summary]);

    const handlePrevious = () => {
        const newDate = new Date(referenceDate);
        newDate.setDate(newDate.getDate() - (periodType === 'weekly' ? 7 : 14));
        setReferenceDate(newDate);
    };

    const handleNext = () => {
        const newDate = new Date(referenceDate);
        newDate.setDate(newDate.getDate() + (periodType === 'weekly' ? 7 : 14));
        setReferenceDate(newDate);
    };

    const formatDateRangeDisplay = () => {
        const start = periodRange.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const end = periodRange.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return `${start} – ${end}`;
    };

    return (
        <div className="flex flex-col h-full bg-gray-50 dark:bg-slate-900">
            {/* Period Selector */}
            <div className="bg-white dark:bg-slate-800 p-4 shadow-sm sticky top-0 z-10">
                <div className="flex items-center justify-center mb-4">
                    <div className="flex bg-gray-100 dark:bg-slate-700 p-1 rounded-xl w-full max-w-xs">
                        <button
                            onClick={() => setPeriodType('weekly')}
                            className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${periodType === 'weekly' ? 'bg-white dark:bg-slate-600 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}
                        >
                            Weekly
                        </button>
                        <button
                            onClick={() => setPeriodType('biweekly')}
                            className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${periodType === 'biweekly' ? 'bg-white dark:bg-slate-600 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}
                        >
                            Biweekly
                        </button>
                    </div>
                </div>

                <div className="flex items-center justify-between px-2">
                    <button onClick={handlePrevious} className="p-2 text-gray-500 hover:text-indigo-600 transition-colors">
                        <Icons.ChevronLeft className="w-6 h-6" />
                    </button>
                    <div className="text-center">
                        <p className="text-sm font-black text-gray-900 dark:text-white tracking-tight">
                            {formatDateRangeDisplay()}
                        </p>
                    </div>
                    <button onClick={handleNext} className="p-2 text-gray-500 hover:text-indigo-600 transition-colors">
                        <Icons.ChevronRight className="w-6 h-6" />
                    </button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="p-4 grid grid-cols-3 gap-3">
                <SummaryCard label="Total Hours" value={summary.totalHours} icon={<Icons.Clock className="w-4 h-4" />} />
                <SummaryCard label="Extra Hours" value={summary.extraHours} icon={<Icons.Zap className="w-4 h-4" />} />
                <SummaryCard label="Work Days" value={summary.workDays.toString()} icon={<Icons.Calendar className="w-4 h-4" />} />
            </div>

            {/* Attendance List */}
            <div className="flex-1 overflow-y-auto px-4 pb-24 space-y-6">
                {processedRecords.length === 0 ? (
                    <div className="text-center py-20">
                        <div className="w-20 h-20 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Icons.Clock className="w-10 h-10 text-gray-300 dark:text-slate-600" />
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 font-medium">No attendance data for this period</p>
                    </div>
                ) : (
                    groupedRecords.map((group, idx) => (
                        <div key={idx} className="space-y-3">
                            {periodType === 'biweekly' && (
                                <div className="flex items-center justify-between px-1">
                                    <h3 className="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">{group.title}</h3>
                                    <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400">{group.totalHours}h</span>
                                </div>
                            )}
                            <div className="space-y-3">
                                {group.records.map(record => (
                                    <AttendanceItem key={record.id} record={record as any} shifts={shifts} />
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

const SummaryCard: React.FC<{ label: string; value: string; icon: React.ReactNode }> = ({ label, value, icon }) => (
    <div className="bg-white dark:bg-slate-800 p-3 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700/50 flex flex-col items-center text-center">
        <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-xl mb-2">
            {icon}
        </div>
        <p className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">{label}</p>
        <p className="text-lg font-black text-gray-900 dark:text-white leading-none">{value}</p>
    </div>
);

const AttendanceItem: React.FC<{ record: AttendanceRecord; shifts: Shift[] }> = ({ record, shifts }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const shift = shifts.find(s => s.id === record.shiftId);

    const statusColors = {
        APPROVED: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400',
        AUTO_APPROVED: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400',
        PENDING: 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400',
        REJECTED: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
    };

    const lateMinutes = calculateMinutesDiff(record.actualStartTime, record.scheduledStartTime);
    const earlyLeave = calculateMinutesDiff(record.scheduledEndTime, record.actualEndTime);

    const dateObj = new Date(record.date + 'T00:00:00');
    const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
    const dayNum = dateObj.getDate();

    return (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700/50 overflow-hidden transition-all">
            <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full p-4 flex items-center gap-4 text-left"
            >
                <div className="text-center w-10 flex-shrink-0">
                    <p className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-0.5">{dayName}</p>
                    <p className="text-xl font-black text-gray-900 dark:text-white leading-none">{dayNum}</p>
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-bold text-gray-900 dark:text-white truncate">{shift?.name || 'Unknown Shift'}</h4>
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest ${statusColors[record.status]}`}>
                            {record.status.replace('_', ' ')}
                        </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs font-medium text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                            <Icons.Clock className="w-3 h-3" />
                            {record.actualStartTime} - {record.actualEndTime}
                        </span>
                        <span className="w-1 h-1 rounded-full bg-gray-300 dark:bg-slate-600"></span>
                        <span className="font-bold text-indigo-600 dark:text-indigo-400">{record.totalHours.toFixed(2)}h</span>
                    </div>
                </div>

                <Icons.ChevronDown className={`w-5 h-5 text-gray-300 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
            </button>

            {isExpanded && (
                <div className="px-4 pb-4 pt-2 border-t border-gray-50 dark:border-slate-700/50 animate-in slide-in-from-top-2 duration-200">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <DetailItem label="Scheduled" value={`${record.scheduledStartTime} - ${record.scheduledEndTime}`} />
                        <DetailItem label="Extra Hours" value={record.extraHours.toFixed(2)} />
                        <DetailItem 
                            label="Late Minutes" 
                            value={lateMinutes > 0 ? `${lateMinutes} min` : 'None'} 
                            valueColor={lateMinutes > 0 ? 'text-red-500' : ''}
                        />
                        <DetailItem 
                            label="Early Leave" 
                            value={earlyLeave > 0 ? `${earlyLeave} min` : 'None'} 
                            valueColor={earlyLeave > 0 ? 'text-orange-500' : ''}
                        />
                    </div>
                    {record.status === 'REJECTED' && (
                        <div className="bg-red-50 dark:bg-red-900/10 p-2 rounded-lg mb-3">
                            <p className="text-[10px] text-red-600 dark:text-red-400 font-bold italic">Using scheduled time (Record Rejected)</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const DetailItem: React.FC<{ label: string; value: string; valueColor?: string }> = ({ label, value, valueColor = '' }) => (
    <div>
        <p className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-0.5">{label}</p>
        <p className={`text-sm font-bold text-gray-900 dark:text-white ${valueColor}`}>{value}</p>
    </div>
);

export default WorkingHoursTab;
